import { FiltersService, type FiltersWidgetStateParams } from '@repo/go-widgets/filters';
import { action, makeObservable, observable, reaction } from 'mobx';

import type { TGroup } from 'src/entities/tab/TabEntity';
import type { IWidgetStore } from 'src/entities/widget/IWidgetStore';
import { requireService } from 'src/packages/di';

import { getFilterCoordinator } from '../filter-coordinator/FilterCoordinator';
import { filtersValuesToFilterState, isFilterStateEmpty } from '../filter-coordinator/filtersValuesToFilterState';
import type { GroupSelectStore } from '../group-select/GroupSelect.store';

import { FiltersWidgetEntity } from './FiltersWidget.entity';

export class FiltersWidgetStore implements IWidgetStore {
  readonly filtersService: FiltersService;

  @observable isGroupSelectOpen: boolean;

  private readonly entity: FiltersWidgetEntity;
  private readonly groupSelectStore: GroupSelectStore;
  private publishedGroupId: string | null = null;

  constructor(
    entity: FiltersWidgetEntity,
    groupSelectStore: GroupSelectStore,
    private readonly notifications = requireService('notifications'),
    directories = requireService('directories'),
  ) {
    this.entity = entity;
    this.groupSelectStore = groupSelectStore;
    this.isGroupSelectOpen = entity.isGroupsDefaultOpened;
    this.filtersService = new FiltersService(directories);
    this.filtersService.hydrate(entity.stateParams);

    entity.setDisposeHostStore(() => this.dispose());

    makeObservable(this);

    this.syncPublishedState();
  }

  private get coordinator() {
    return getFilterCoordinator(this.groupSelectStore);
  }

  private syncPublishedState(): void {
    const groupId = this.entity.groupId;
    const nextState = filtersValuesToFilterState(this.filtersService.applied);

    if (this.publishedGroupId && this.publishedGroupId !== groupId) {
      this.coordinator.clear(this.publishedGroupId);
      this.publishedGroupId = null;
    }

    if (!groupId) {
      return;
    }

    if (isFilterStateEmpty(nextState)) {
      if (this.publishedGroupId === groupId) {
        this.coordinator.clear(groupId);
        this.publishedGroupId = null;
      }
      return;
    }

    this.coordinator.publish(groupId, nextState);
    this.publishedGroupId = groupId;
  }

  @action.bound
  setGroupForFilters(group: TGroup | null): void {
    if (group === null) {
      if (this.publishedGroupId) {
        this.coordinator.clear(this.publishedGroupId);
        this.publishedGroupId = null;
      }
      this.entity.setGroup(null);
      return;
    }

    const groupedWidgets = this.groupSelectStore.groupManager.getWidgetsByGroupId(group.id);
    const existingFilter = groupedWidgets?.find(
      (widget) => widget instanceof FiltersWidgetEntity && widget !== this.entity,
    );

    if (existingFilter) {
      this.notifications.showErrorMessageT('errors:wellsFilterAlreadyExistsInGroup');
      return;
    }

    this.entity.setGroup(group.id);
    this.syncPublishedState();
  }

  @action.bound
  setIsGroupSelectOpen(isOpen: boolean): void {
    this.isGroupSelectOpen = isOpen;
  }

  @action.bound
  setStateParams(stateParams: FiltersWidgetStateParams): void {
    this.entity.setStateParams(stateParams);
    this.syncPublishedState();
  }

  effect = () => {
    const disposeGroup = reaction(
      () => this.entity.groupId,
      () => {
        this.syncPublishedState();
      },
    );

    return () => {
      disposeGroup();
    };
  };

  @action.bound
  dispose(): void {
    if (this.publishedGroupId) {
      this.coordinator.clear(this.publishedGroupId);
      this.publishedGroupId = null;
    }

    this.filtersService.dispose();

    const storeService = requireService('widgetStoreService');
    storeService.removeStore(this.entity);
  }
}
